import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { FaExclamationCircle, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { selectImageIds, selectImageIdsState } from '../../Store/imageSlice';
import { useSelector, useDispatch } from 'react-redux';
import useLocalResultData from './hooks/useLocalResultData';
import { useFetchProjectQuery } from '../../api/project';
import { useImageList } from './hooks/useImageList';
import StarToggle from '../Favorite/StarToggle';

interface Image {
    id: string;
    name: string;
    description: string;
    created_by: string;
    owner_id: string;
    s3_path: string;
    status: string;
    tags: string;
    upload_status: string;
    tile_status: string;
    tile_status_reason?: string;
    tile_status_category?: string;
    updated_at: string;
}

interface ImageListProps {
    projectId: string;
}

// Backend categories (see ai-services failure_classifier.py) mapped to
// copy a non-technical lab user can act on - never the raw libvips/S3
// error text. Falls back to the system-error message for any category
// we don't recognize, since it's safer to say "we're on it" than to
// wrongly blame the user's file.
const FAILURE_CATEGORY_MESSAGES: Record<string, string> = {
    invalid_input:
        "There's a problem with this slide file — it may be incomplete, corrupted, or in a format we can't read. Please check the file and try uploading it again.",
    system_error:
        "Something went wrong on our end while processing this slide. Please try again, or contact support if it keeps happening.",
};
const DEFAULT_FAILURE_MESSAGE = FAILURE_CATEGORY_MESSAGES.system_error;

type SortField = 'quality' | 'result' | 'updated' | null;
type SortDirection = 'asc' | 'desc' | null;
type StatusValue = 'In Progress' | 'Completed' | 'Pass' | 'Needs Review' | 'Skipped' | 'Failed' | 'Not Started' | 'N/A' | 'Incomplete';

const statusOrder: Record<StatusValue, number> = {
    'In Progress': 0,
    'Completed': 1,
    'Pass': 2,
    'Needs Review': 3,
    'Skipped': 4,
    'Failed': 5,
    'Not Started': 6,
    'N/A': 7,
    'Incomplete': 8
};

export default function ImageList(props: ImageListProps) {
    const { projectId } = props;
    const { data: project } = useFetchProjectQuery(projectId);
    const selectedImageIds = useSelector(selectImageIdsState);
    const { resultData } = useLocalResultData({ projectId });
    const [sortField, setSortField] = useState<SortField>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);
    const dispatch = useDispatch();
    
    // Use the new reusable hook
    const { 
        images, 
        isLoading: dataLoading, 
        isLoadingNext,
        isError: dataError, 
        hasMore, 
        fetchNext 
    } = useImageList({ 
        projectId, 
        autoFetchAll: false // Manual pagination control
    });

    const hasLocalResultData = Object.keys(resultData).length;

    const getQualityStatus = useCallback((imageId: string) => {
        const results = resultData[imageId];
        if (!results || results.length === 0) return { status: 'N/A', percentages: null };
        
        // Find the quality detection result
        const qualityResult = results.find(result => 
            result.model_path?.toLowerCase().includes('quality') && 
            result.status === 'Completed'
        );
        
        if (!qualityResult) return { status: 'N/A', percentages: null };
        
        // Get percentages from the correct structure
        const poorPercentage = qualityResult.result_data?.[0]?.['AI-Measurement']?.['withoutBackground']?.['PoorPercentage'];
        const goodPercentage = qualityResult.result_data?.[0]?.['AI-Measurement']?.['withoutBackground']?.['GoodPercentage'];
        
        if (poorPercentage === undefined || goodPercentage === undefined) {
            return { status: 'Incomplete', percentages: null };
        }
        
        const poorTolerance = project?.poor_tolerance * 100 || 30; // Default to 30% if not set
        
        return {
            status: poorPercentage <= poorTolerance ? 'Pass' : 'Needs Review',
            percentages: `${(goodPercentage ?? 0).toFixed(2)}%/${(poorPercentage ?? 0).toFixed(2)}%`
        };
    }, [resultData, project?.poor_tolerance]);

    const getResultStatus = useCallback((imageId: string) => {
        const results = resultData[imageId];
        if (!results || results.length === 0) return 'Not Started';
        
        // Find semantic segmentation results
        const segmentationResults = results.filter(result => 
            result.model_path?.toLowerCase().includes('semantic')
        );
        
        if (segmentationResults.length === 0) return 'Not Started';
        
        // Sort by updated_at to get the latest result
        const latestResult = segmentationResults.sort((a, b) => 
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        )[0];
        
        // Return status based on the latest result
        switch (latestResult.status) {
            case 'In Progress':
                return 'In Progress';
            case 'Completed':
                return 'Completed';
            case 'Skip':
                return 'Skipped';
            case 'Failed':
                return 'Failed';
            default:
                return 'Not Started';
        }
    }, [resultData]);

    const sortedImages = useMemo(() => {
        if (!images) return [];
        
        let imageList = [...images];
        
        if (sortField && sortDirection) {
            imageList.sort((a, b) => {
                if (sortField === 'updated') {
                    const aDate = new Date(a.updated_at).getTime();
                    const bDate = new Date(b.updated_at).getTime();
                    return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
                }
                
                const aStatus = (sortField === 'quality' ? 
                    getQualityStatus(a.id).status : 
                    getResultStatus(a.id)) as StatusValue;
                const bStatus = (sortField === 'quality' ? 
                    getQualityStatus(b.id).status : 
                    getResultStatus(b.id)) as StatusValue;
                
                const aOrder = statusOrder[aStatus] ?? 999;
                const bOrder = statusOrder[bStatus] ?? 999;
                
                return sortDirection === 'asc' ? 
                    aOrder - bOrder : 
                    bOrder - aOrder;
            });
        }
        
        return imageList;
    }, [images, sortField, sortDirection, getQualityStatus, getResultStatus]);

    useEffect(() => {
        console.log('ImageList dataLoading state changed:', dataLoading);
    }, [dataLoading]);

    useEffect(() => {
        return () => {
            dispatch(selectImageIds([]));
        }
    }, [dispatch]);

    useEffect(() => {
        // Create a sentinel element to observe
        const sentinel = document.createElement('div');
        sentinel.style.height = '1px';
        sentinel.style.width = '100%';
        sentinel.style.position = 'relative';
        sentinel.style.top = '-200px'; // Position it 200px above the bottom of the list

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && hasMore && !isLoadingNext) {
                    fetchNext();
                }
            },
            {
                root: null, // viewport
                rootMargin: '0px',
                threshold: 0.1 // Trigger when at least 10% of the sentinel is visible
            }
        );

        // Add the sentinel to the DOM after the table
        const tableContainer = document.querySelector('.overflow-x-auto');
        if (tableContainer) {
            tableContainer.appendChild(sentinel);
            observer.observe(sentinel);
        }

        return () => {
            observer.disconnect();
            if (tableContainer && sentinel.parentNode === tableContainer) {
                tableContainer.removeChild(sentinel);
            }
        };
    }, [hasMore, isLoadingNext, fetchNext]);

    if (dataLoading && images.length === 0) return <div className='text-center mt-6'><LoadingSpiner/></div>;
    if (dataError) return <div><p className='text-center mt-6'><FaExclamationCircle className='inline mr-2'/> Error loading images</p></div>;

    const handleImageSelect = (imageId: string) => {
        const currentIndex = selectedImageIds.indexOf(imageId);
        const newSelectedImages = [...selectedImageIds];

        if (currentIndex === -1) {
            newSelectedImages.push(imageId);
        } else {
            newSelectedImages.splice(currentIndex, 1);
        }

        dispatch(selectImageIds(newSelectedImages));
    };

    // A still-tiling image cannot be analysed or opened, so it must not be
    // selectable — select-all used to sweep them in and submit work the server
    // rejects, and pending upload stand-ins made that the common case.
    const selectableImages = images.filter((image: Image) => image.tile_status !== 'In Progress');

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && selectableImages.length) {
            dispatch(selectImageIds(selectableImages.map((image: Image) => image.id)));
            return;
        }
        dispatch(selectImageIds([]));
    };

    const isAllSelected = selectableImages.length > 0 && selectableImages.length === selectedImageIds.length;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return <FaSort className="text-gray-400" />;
        return sortDirection === 'asc' ? 
            <FaSortUp className="text-blue-500" /> : 
            <FaSortDown className="text-blue-500" />;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="overflow-x-auto mt-6">
            <div className="container">
                <p>{images.length} Images</p>
            </div>
            <table className="table w-full">
                <thead>
                    <tr>
                        <th className="w-12">
                            <label>
                                <input
                                    type="checkbox"
                                    className="checkbox"
                                    onChange={handleSelectAllClick}
                                    checked={isAllSelected}
                                />
                            </label>
                        </th>
                        <th className="w-12"></th>
                        <th>Image Name</th>
                        <th>Upload Status</th>
                        <th>Image Status</th>
                        {hasLocalResultData ? (
                            <th 
                                className="cursor-pointer hover:bg-base-200"
                                onClick={() => handleSort('result')}
                            >
                                <div className="flex items-center">
                                    Segmentation Status
                                    <span className="ml-1">{getSortIcon('result')}</span>
                                </div>
                            </th>
                        ) : null}
                        {hasLocalResultData ? (
                            <th 
                                className="cursor-pointer hover:bg-base-200"
                                onClick={() => handleSort('quality')}
                            >
                                <div className="flex items-center">
                                    Quality Status
                                    <span className="ml-1">{getSortIcon('quality')}</span>
                                </div>
                            </th>
                        ) : null}
                        <th 
                            className="cursor-pointer hover:bg-base-200"
                            onClick={() => handleSort('updated')}
                        >
                            <div className="flex items-center">
                                Last Updated
                                <span className="ml-1">{getSortIcon('updated')}</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedImages.map((image: Image) => {
                        const displayedStatus = image.tile_status === 'In Progress' ? "Preprocessing" : image.tile_status === 'Done' ? "Ready" : image.tile_status === 'Failed' ? "Failed" : image.upload_status;
                        const resultStatus = getResultStatus(image.id);
                        const qualityStatus = getQualityStatus(image.id);
                        
                        return <tr key={image.id}>
                            <th className="w-12">
                                <label>
                                    <input
                                        type="checkbox"
                                        className="checkbox"
                                        onChange={() => handleImageSelect(image.id)}
                                        checked={selectedImageIds.includes(image.id)}
                                        disabled={displayedStatus === "Preprocessing"}
                                        title={displayedStatus === "Preprocessing" ? "Still preprocessing" : undefined}
                                    />
                                </label>
                            </th>
                            <td className="w-12">
                                <StarToggle entityId={image.id} entityType="Image" />
                            </td>
                            <td>
                                <div className="flex items-center space-x-3">
                                    <div>
                                        {displayedStatus === "Preprocessing" ? (
                                            <span className="text-base-content/50 cursor-not-allowed">{image.name}</span>
                                        ) : (
                                            <Link to={`images/${image.id}`}>{image.name}</Link>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div className="flex item-center space-x3">
                                    <div>{image.upload_status}</div>
                                </div>
                            </td>
                            <td>
                                <div className="flex items-center space-x-3">
                                    <div
                                        className={displayedStatus === 'Failed' ? 'text-red-500 tooltip' : ''}
                                        data-tip={displayedStatus === 'Failed'
                                            ? (image.tile_status_category
                                                ? FAILURE_CATEGORY_MESSAGES[image.tile_status_category] ?? DEFAULT_FAILURE_MESSAGE
                                                : DEFAULT_FAILURE_MESSAGE)
                                            : undefined}
                                    >
                                        {displayedStatus}
                                    </div>
                                </div>
                            </td>
                            {hasLocalResultData ? <td>
                                <div className="flex items-center space-x-3">
                                    <div>{resultStatus}</div>
                                </div>
                            </td>: null}
                            {hasLocalResultData ? <td>
                                <div className="flex items-center space-x-3">
                                    <div className={`${qualityStatus.status === 'Needs Review' ? 'text-amber-500' : qualityStatus.status === 'Pass' ? 'text-green-500' : ''} tooltip`} data-tip={qualityStatus.percentages || ''}>
                                        {qualityStatus.status}
                                    </div>
                                </div>
                            </td>: null}
                            <td>
                                <div>{formatDate(image.updated_at)}</div>
                            </td>
                        </tr>
                    })}
                </tbody>
            </table>
            
            {/* Loading indicator for pagination */}
            {hasMore && (
                <div className="text-center py-4">
                    {isLoadingNext && <LoadingSpiner />}
                    <p className="text-sm text-gray-500 mt-2">Retrieving additional images for review...</p>
                </div>
            )}
            
            {/* End of list indicator */}
            {!hasMore && images.length > 0 && (
                <div className="text-center py-4">
                    <p className="text-sm text-gray-500">All images have been loaded</p>
                </div>
            )}
        </div>
    );
}
